// Кастомный календарь в iOS-стиле, без зависимостей.
// Принимает/возвращает дату в формате YYYY-MM-DD.

import { useEffect, useMemo, useRef, useState } from "react";
import { haptic } from "../lib/telegram";

interface Props {
  value?: string; // YYYY-MM-DD
  onChange: (v: string) => void;
  placeholder?: string;
  error?: boolean;
}

const MONTHS = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];
const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

function pad(n: number): string {
  return n < 10 ? "0" + n : String(n);
}

function parseDate(v?: string): Date | null {
  if (!v) return null;
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDate(d: Date): string {
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function toISO(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function DatePicker({ value, onChange, placeholder = "Выбрать дату", error }: Props) {
  const selected = parseDate(value);
  const today = useMemo(() => new Date(), []);
  const [open, setOpen] = useState(false);
  // Месяц, который сейчас показан в календаре
  const [view, setView] = useState<Date>(selected ?? today);
  const containerRef = useRef<HTMLDivElement>(null);

  // Закрываем по клику вне компонента
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // При повторном открытии — встаём на выбранный месяц (или текущий)
  useEffect(() => {
    if (open) setView(selected ?? today);
  }, [open]);

  // Сетка дней: с понедельника недели, в которой начинается месяц,
  // до воскресенья недели, в которой заканчивается.
  const cells = useMemo(() => {
    const y = view.getFullYear();
    const m = view.getMonth();
    const first = new Date(y, m, 1);
    // День недели: JS отдаёт 0=Вс, нам нужно 0=Пн
    const firstDow = (first.getDay() + 6) % 7;
    const start = new Date(y, m, 1 - firstDow);

    const list: Date[] = [];
    for (let i = 0; i < 42; i++) {
      list.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
    }
    return list;
  }, [view]);

  const display = selected ? formatDate(selected) : "";

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => { haptic("light"); setOpen((o) => !o); }}
        className={`ios-input text-left flex items-center justify-between ${error ? "ios-input-error" : ""}`}
      >
        <span className={display ? "" : "text-muted"}>{display || placeholder}</span>
        <span className="text-muted text-base">📅</span>
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-[280px] bg-card rounded-2xl shadow-xl border border-separator overflow-hidden" style={{ boxShadow: "0 10px 40px rgba(0,0,0,0.15), 0 0 0 0.5px rgba(0,0,0,0.06)" }}>
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-3 border-b border-separator">
            <button
              type="button"
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-inset active:opacity-60"
              onClick={() => { haptic("light"); setView(new Date(view.getFullYear(), view.getMonth() - 1, 1)); }}
            >
              ‹
            </button>
            <div className="text-[15px] font-semibold text-ink">
              {MONTHS[view.getMonth()]} {view.getFullYear()}
            </div>
            <button
              type="button"
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-inset active:opacity-60"
              onClick={() => { haptic("light"); setView(new Date(view.getFullYear(), view.getMonth() + 1, 1)); }}
            >
              ›
            </button>
          </div>

          {/* Weekdays */}
          <div className="grid grid-cols-7 px-2 pt-2 pb-1 text-center text-[11px] font-medium text-muted uppercase">
            {WEEKDAYS.map((w) => (
              <div key={w} className="py-1">{w}</div>
            ))}
          </div>

          {/* Days */}
          <div className="grid grid-cols-7 px-2 pb-3 gap-y-1">
            {cells.map((d, i) => {
              const inCurrentMonth = d.getMonth() === view.getMonth();
              const isSelected = selected ? sameDay(d, selected) : false;
              const isToday = sameDay(d, today);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => {
                    haptic("light");
                    onChange(toISO(d));
                    setOpen(false);
                  }}
                  className={[
                    "h-9 w-9 mx-auto rounded-full text-[14px] font-medium transition active:scale-90",
                    isSelected
                      ? "bg-accent text-white"
                      : isToday
                        ? "text-accent border border-accent/40"
                        : inCurrentMonth
                          ? "text-ink hover:bg-inset"
                          : "text-muted/50 hover:bg-inset/50",
                  ].join(" ")}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="border-t border-separator px-3 py-2 flex justify-between">
            <button
              type="button"
              className="text-[14px] text-muted hover:text-ink active:opacity-60"
              onClick={() => { haptic("light"); onChange(""); setOpen(false); }}
            >
              Сбросить
            </button>
            <button
              type="button"
              className="text-[14px] text-accent font-semibold active:opacity-60"
              onClick={() => { haptic("light"); onChange(toISO(today)); setOpen(false); }}
            >
              Сегодня
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
